import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PcmpDivision } from '../types/pcmpQueue.types';
import type { PcmpReportFilters, PcmpReportSearch } from '../types/pcmpReports.types';

interface PcmpReportDraftFilters {
  projectId: number | undefined;
  division: PcmpDivision | undefined;
  departmentId: number | undefined;
  dateFrom: string;
  dateTo: string;
}

const INITIAL_FILTERS: PcmpReportDraftFilters = {
  projectId: undefined,
  division: undefined,
  departmentId: undefined,
  dateFrom: '',
  dateTo: '',
};

// Every filter field of the URL cleared, so an apply or reset replaces them all.
const CLEARED_SEARCH_FILTERS = {
  projectId: undefined,
  division: undefined,
  departmentId: undefined,
  dateFrom: undefined,
  dateTo: undefined,
} as const satisfies PcmpReportSearch;

const NO_EXTRA = { activeCount: 0, cleared: {} };

/**
 * The draft-then-apply filter flow every PCMP report shares. The report's route
 * hook owns the URL and any params of its own (the Pending stage and the like).
 */
export function usePcmpReportFilters<S extends PcmpReportSearch>(
  search: S,
  setSearch: (update: (prev: S) => S) => void,
  /** The report's own params (a stage, a member): how many are set and how to clear them. */
  extra: { activeCount: number; cleared: Partial<S> } = NO_EXTRA,
) {
  const appliedFilters = useMemo<PcmpReportFilters>(
    () => ({
      projectId: search.projectId,
      division: search.division,
      departmentId: search.departmentId,
      dateFrom: search.dateFrom,
      dateTo: search.dateTo,
    }),
    [search.projectId, search.division, search.departmentId, search.dateFrom, search.dateTo],
  );

  const appliedDraft = useMemo<PcmpReportDraftFilters>(
    () => ({
      projectId: appliedFilters.projectId,
      division: appliedFilters.division,
      departmentId: appliedFilters.departmentId,
      dateFrom: appliedFilters.dateFrom ?? '',
      dateTo: appliedFilters.dateTo ?? '',
    }),
    [appliedFilters],
  );

  const [draftFilters, setDraftFilters] = useState<PcmpReportDraftFilters>(appliedDraft);

  useEffect(() => {
    setDraftFilters(appliedDraft);
  }, [appliedDraft]);

  const { activeCount: extraActiveCount, cleared: extraCleared } = extra;
  const activeFilterCount =
    Object.values(appliedFilters).filter((value) => value !== undefined).length + extraActiveCount;

  const isDateRangeInvalid =
    draftFilters.dateFrom !== '' &&
    draftFilters.dateTo !== '' &&
    draftFilters.dateFrom > draftFilters.dateTo;

  const setDraft = useCallback(
    <K extends keyof PcmpReportDraftFilters>(key: K, value: PcmpReportDraftFilters[K]) => {
      setDraftFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    if (isDateRangeInvalid) return;
    setSearch((prev) => ({
      ...prev,
      projectId: draftFilters.projectId,
      division: draftFilters.division,
      departmentId: draftFilters.departmentId,
      dateFrom: draftFilters.dateFrom || undefined,
      dateTo: draftFilters.dateTo || undefined,
    }));
  }, [draftFilters, isDateRangeInvalid, setSearch]);

  const handleReset = useCallback(() => {
    setDraftFilters(INITIAL_FILTERS);
    setSearch((prev) => ({ ...prev, ...CLEARED_SEARCH_FILTERS, ...extraCleared }));
  }, [setSearch, extraCleared]);

  return {
    filters: draftFilters,
    appliedFilters,
    activeFilterCount,
    isDateRangeInvalid,
    setDraft,
    handleApply,
    handleReset,
  };
}

export type PcmpReportFilterController = ReturnType<typeof usePcmpReportFilters>;
